import styled from 'styled-components';
import { GridColDef, GridPagination, GridPaginationModel } from '@mui/x-data-grid';
import Table from '../custom/Table';
import Title from '../custom/Title';
import { TRedeemTransactionSummary, TTransactionSummary, TTransactionsFilters } from '../../Models/Transactions';
import { useContext, useEffect, useState } from 'react';
import { EcomTransactionContext } from '../../contexts/ecomOrderContext';
import useService from '../../hooks/useService';
import { getEcomRedeemAtPvcOrders, getPvcCenters, getTransactions, getTransactionsCount } from '../../services/Transactions';
import FiltersForm, { initialTransactionFilters } from './FiltersForm';
import { IconButton, Tooltip } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataFormat, PATHS } from '../../utils/constant';
import { TOption, TTransactionTypes } from '../../Models/common';
import moment from 'moment';
import { PageContext } from '../../contexts/PageContext';
import { ICustomerBasic } from '../../Models/Customer';
import { CustomerContext } from '../../contexts/CustomerContext';

const Divider = styled.div`
    border: 1px solid #EBEBEC;
`

//Redeem transaction table columns --
const REDEEM_TRANSACTIONS_COLUMNS: GridColDef<TRedeemTransactionSummary>[] = [
    {
        field: "action",
        headerName: 'Action',
        hideSortIcons: true
    },
    {
        field: "orderId",
        headerName: 'Order ID',
        minWidth: 250,
    },
    {
        field: "transactionType",
        headerName: 'Order Type',
        minWidth: 150,
    },
    {
        field: "status",
        headerName: 'Status',
        minWidth: 180,
    },
    {
        field: "full_name",
        headerName: 'Customer Name',
        minWidth: 180,
        renderCell(params) {
            return params.row?.customer?.fullName
        },
    },
    {
        field: "customer_ref_no",
        headerName: 'Customer Ref No',
        minWidth: 150,
        renderCell(params) {
            return params.row?.customer?.customerRefNo
        },
    },
    {
        field: "platform",
        headerName: 'Platform',
        minWidth: 180,
        renderCell(params) {
            return params?.row?.platform
        },
    },
    {
        field: "payment_info_id",
        headerName: 'Payment Info ID',
        minWidth: 280,
        renderCell(params) {
            return params.row?.payment_info_id || 'N/A'
        },
    },
    {
        field: "created_at",
        headerName: 'Creation Date',
        minWidth: 200,
        renderCell(params) {
            const date = params.row.createdAt
            return date ? moment(date).format(DataFormat) : 'N/A'
        },
    },
    {
        field: "remarks",
        headerName: 'Remark',
        minWidth: 350,
        renderCell(params) {
            return params.row.remarks ? params.row.remarks : 'N/A'
        },
    }
] 

const getViewUrl = (transactionType: TTransactionTypes, id: string) => {
    let path
    switch (transactionType) {
        case 'BUY_BACK':
            path = PATHS.BUY_BACK
            break;

        case 'BUY_SELL':
            path = PATHS.BUY_SELL
            break;

        case 'GMS':
            path = PATHS.GMS
            break;

        case 'JOB_WORK':
            path = PATHS.JOB_WORK
            break;

        case 'PURITY_TESTING':
            path = PATHS.PURITY_TESTING
            break;
        case 'MINTED_SALES':
            path = PATHS.MINTED_SALES
            break;
        case 'REDEEM_AT_PVC':
            path = PATHS.REDEEM_AT_PVC
            break;
    }

    return path + '/' + id
}

export const RedeemAtPVCTransaction = () => {
    const navigate = useNavigate()
    const { transactions } = useContext(EcomTransactionContext)
    const { setPageState } = useContext(PageContext)
    const { execute } = useService()
    const [count, setCount] = useState(0)
    const [filters, setFilters] = useState<TTransactionsFilters>(initialTransactionFilters)
    const { setCustomerConfig } = useContext(CustomerContext)
    
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10
    })

    const getColumns = () => {
        return REDEEM_TRANSACTIONS_COLUMNS.map<GridColDef<TRedeemTransactionSummary>>(column => {
            return column.field === 'action' ?
                {
                    ...column,
                    renderCell(params) {
                        return <Tooltip title='View Transaction'>
                            <IconButton onClick={() => {
                                setCustomerConfig({} as ICustomerBasic)
                                navigate(getViewUrl('REDEEM_AT_PVC' as TTransactionTypes, params.row.orderId))
                            }}>
                                <Visibility />
                            </IconButton>
                        </Tooltip>
                    },
                } : column
        })
    }

    const getCount = async () => {
        const { data } = await execute(() => getTransactionsCount(filters))
        if (data) setCount(data.count)
    }

    const getLocation = () => {
        execute(() => getPvcCenters({}))
    }

    useEffect(() => {
        setPageState({
            title: 'Redeem at PVC Transactions',
            currentPage: 'Redeem at PVC Transactions',
            breadcrumbs: [],
            path: PATHS.REDEEM_AT_PVC_TRANSACTION
        })
        getLocation()
    }, [])

    useEffect(() => {
        if(!filters?.isRedeemAtPvc)
        setFilters(prev => ({ ...prev, isRedeemAtPvc: true }))
        getCount()
    }, [filters])

    useEffect(() => {
        execute(() => getEcomRedeemAtPvcOrders({ ...filters, pageNumber: paginationModel.page, pageSize: paginationModel.pageSize }))
    }, [paginationModel, filters])

    return (
        <div style={{ padding: '0 10px' }}>
            <div style={{ borderBottom: '1px solid #d8d8d8' }}>
                <Title style={{ textAlign: 'left', margin: '20px 10px 10px 10px', color: '#7f7d76' }}>ECOM Redeem at PVC Transaction Blotter</Title>
            </div>
            <div style={{ padding: '20px 0px' }}>
                <FiltersForm setFilters={(params : any) => setFilters(params)} />
                <div></div>
            </div>
            <div style={{ overflow: 'hidden' }}>
                <Table
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => setPaginationModel(model)}
                    pageSizeOptions={[10, 50, 100]}
                    columns={getColumns()}
                    rows={transactions}
                    getRowId={(row) => row.orderId}
                    hideFooter={false}
                    rowCount={count}
                    paginationMode='server'
                    disableColumnFilter
                />
            </div>
        </div>
    )
}
